import React, {useCallback, useEffect, useState} from "react";
import {Box, Button, CloseButton, Flex, IconButton, Text} from "@chakra-ui/react";
import {AiOutlinePlus} from "react-icons/ai";
import iso6391 from "iso-639-1";
import {usePreviewQC} from "../../context/usePreviewQC.ts";
import {useProjectsStore} from "../../store/useProjectsStore.ts";
import QCNotesBody from "./QCNotesBody.tsx";

interface TranslationQCPanelProps {
  projectId: string | null;
  onClose: () => void;
  showPreviewControls?: boolean;
}

const languageButtonStyles = {size: "sm", variant: "outline", color: "white", borderColor: "gray.600", fontSize: "xs", fontWeight: "medium", px: 3, h: 10, minW: 10, _hover: {bg: "gray.700"}, _active: {transform: "scale(0.96)"}, transitionProperty: "background-color, border-color, transform", transitionDuration: "0.2s"};

const TranslationQCPanel: React.FC<TranslationQCPanelProps> = ({projectId, onClose, showPreviewControls = false}) => {
  const projects = useProjectsStore((state) => state.projects);
  const {isPreviewSessionActive, sourceLanguageCode, translationLanguages, activeTranslationLanguage, playerRef, setProjectLanguages, openTranslationLanguage, closeTranslationLanguage, addNoteRow, loadQCNotes} = usePreviewQC();
  const [selectedReadOnlyLanguage, setSelectedReadOnlyLanguage] = useState<string | null>(null);
  const [scrollTargetNoteId, setScrollTargetNoteId] = useState<string | null>(null);
  const clearScrollTarget = useCallback(() => setScrollTargetNoteId(null), []);
  const canAddNotes = showPreviewControls && isPreviewSessionActive;
  const qcLanguages = sourceLanguageCode ? [sourceLanguageCode, ...translationLanguages.filter((code) => code !== sourceLanguageCode)] : translationLanguages;
  const selectedLanguage = canAddNotes ? activeTranslationLanguage : selectedReadOnlyLanguage;

  useEffect(() => {
    if (!projectId) return;
    const project = projects.find((item) => item.id === projectId);
    if (project) setProjectLanguages(project.sourceLanguage, Object.keys(project.featuresStates.subtitleTranslations ?? {}));
  }, [projectId, projects, setProjectLanguages]);

  useEffect(() => {
    if (projectId) void loadQCNotes(projectId);
  }, [loadQCNotes, projectId]);

  useEffect(() => {
    setSelectedReadOnlyLanguage(null);
  }, [projectId]);

  const selectLanguage = (languageCode: string) => {
    if (!canAddNotes) {
      setSelectedReadOnlyLanguage(languageCode);
      return;
    }
    if (activeTranslationLanguage === languageCode) {
      closeTranslationLanguage();
      return;
    }
    openTranslationLanguage(languageCode);
  };

  return (
    <Flex direction="column" h="full" bg="gray.800" borderLeft="1px solid" borderColor="gray.700">
      <Flex color="white" borderBottom="1px solid" borderColor="gray.700" align="flex-start" justify="space-between" py={3} px={4} flexShrink={0} gap={2}>
        <Flex align="center" flexWrap="wrap" gap={2} flex="1" minW={0}>
          <Text fontSize="lg" fontWeight="semibold" flexShrink={0}>Translation QC</Text>
          {canAddNotes && <IconButton aria-label="Add QC note" icon={<AiOutlinePlus size={20} />} size="sm" minW={10} minH={10} variant="outline" color="white" borderColor="gray.600" _hover={{bg: "gray.700"}} _active={{transform: "scale(0.96)"}} transitionProperty="background-color, transform" transitionDuration="0.2s" onClick={(event) => {
            event.stopPropagation();
            const targetLanguage = activeTranslationLanguage ?? sourceLanguageCode;
            if (!targetLanguage) return;
            if (!activeTranslationLanguage) openTranslationLanguage(targetLanguage);
            playerRef.current?.pause();
            setScrollTargetNoteId(addNoteRow(targetLanguage));
          }} />}
          {qcLanguages.map((languageCode) => <Button key={languageCode} {...languageButtonStyles} borderColor={selectedLanguage === languageCode ? "brandPurple.500" : "gray.600"} bg={selectedLanguage === languageCode ? "gray.700" : "transparent"} onClick={() => selectLanguage(languageCode)}>{iso6391.getName(languageCode)}</Button>)}
        </Flex>
        <CloseButton aria-label="Close Translation QC" color="white" flexShrink={0} minW={10} minH={10} _hover={{bg: "gray.700"}} _active={{transform: "scale(0.96)"}} transitionProperty="background-color, transform" transitionDuration="0.2s" onClick={onClose} />
      </Flex>
      <Box flex="1" minH={0} overflowY="auto" py={4} px={4}>
        {selectedLanguage ? <QCNotesBody languageCode={selectedLanguage} canAddNotes={canAddNotes} scrollTargetNoteId={scrollTargetNoteId} onScrollTargetHandled={clearScrollTarget} /> : <Text color="gray.400" fontSize="sm">Select a language to view QC notes.</Text>}
      </Box>
    </Flex>
  );
};

export default TranslationQCPanel;
